import { useEffect, useState } from 'react';

import { Certificate } from '@revenuemarket/bank';
import { Button, Colors, Dropdown, Flex, Input as CDSInput, Text } from '@revenuemarket/cds';
import { ResponseStatus } from '@revenuemarket/http';
import { Toast } from '@revenuemarket/ui';
import { deformatCompanyTaxNumber, formatCompanyTaxNumber } from '@revenuemarket/utils';
import { BusinessRegistrationNumberInfo } from '~apis/bank/getBusinessRegistrationNumbers';
import { useBusinessRegistrationNumbers } from '~hooks/queries/useBusinessRegistrationNumbers';
import useDeleteCertificationsMutation from '~hooks/queries/useDeleteCertificationsMutation';
import useUpdateCertificateBusinessRegNumberMutation from '~hooks/queries/useUpdateCertificateBusinessRegNumberMutation';
import { useCompanyId } from '~hooks/useCompanyId';
import { validateBusinessRegNumber } from '~utils/businessRegNumber/validator';

import CommonModal from '../CommonModal';

interface BusinessRegNumberInputModalProps {
  isOpen: boolean;
  onClose: () => void;
  certificate: Certificate;
  onSuccess: () => void;
}

const BusinessRegNumberInputModal = ({
  isOpen,
  onClose,
  certificate,
  onSuccess,
}: BusinessRegNumberInputModalProps) => {
  const { companyId } = useCompanyId();
  const [businessRegNumber, setBusinessRegNumber] = useState('');
  const [businessNumberError, setBusinessNumberError] = useState<string | undefined>();

  const { data: businessNumbersData } = useBusinessRegistrationNumbers();
  const { mutateAsync: deleteCertificate, isLoading: isDeleting } =
    useDeleteCertificationsMutation();
  const { mutateAsync: updateBusinessRegNumber, isLoading: isUpdating } =
    useUpdateCertificateBusinessRegNumberMutation();

  // 첫 번째 사업자번호로 자동 채우기
  useEffect(() => {
    const firstBusinessNumber =
      businessNumbersData?.Data?.data?.businessRegNumbers?.[0]?.businessRegNumber;
    if (firstBusinessNumber && !businessRegNumber) {
      setBusinessRegNumber(firstBusinessNumber);
      // 첫 번째 값도 검증
      const validation = validateBusinessRegNumber(firstBusinessNumber);
      setBusinessNumberError(validation.error);
    }
  }, [businessNumbersData, businessRegNumber]);

  const handleBusinessNumberChange = (value: string) => {
    setBusinessRegNumber(value);

    if (value.length > 0) {
      const validation = validateBusinessRegNumber(value);
      setBusinessNumberError(validation.error);
    } else {
      setBusinessNumberError(undefined);
    }
  };

  const handleBusinessNumberSelect = (value: string) => {
    setBusinessRegNumber(value);
    const validation = validateBusinessRegNumber(value);
    setBusinessNumberError(validation.error);
  };

  const handleClose = async () => {
    try {
      await deleteCertificate({
        companyId,
        certificateId: certificate.certificateId,
      });
      Toast.success('공동인증서가 삭제되었습니다.');
      onClose();
    } catch (error) {
      Toast.error('공동인증서 삭제에 실패했습니다.');
    }
  };

  const handleConfirm = async () => {
    try {
      const numbers = businessRegNumber.replace(/[^\d]/g, '');

      const response = await updateBusinessRegNumber({
        companyId,
        certificateId: certificate.certificateId,
        businessRegNumber: numbers,
      });

      if (response?.Status === ResponseStatus.SUCCESS) {
        Toast.success('사업자등록번호가 등록되었습니다.');
        onSuccess();
        onClose();
      } else {
        Toast.error('사업자등록번호 등록에 실패했습니다.');
      }
    } catch (error) {
      Toast.error('사업자등록번호 등록에 실패했습니다.');
    }
  };

  const validation = validateBusinessRegNumber(businessRegNumber);
  const loading = isDeleting || isUpdating;

  return (
    <CommonModal
      width={400}
      open={isOpen}
      onCancel={handleClose}
      footer={null}
      closable={false}
      maskClosable={false}
      title={
        <Flex justify="space-between" align="center" style={{ width: '100%' }}>
          <Text textStyle="16_600" style={{ color: Colors.gray800 }}>
            사업자등록번호를 입력해주세요
          </Text>
          <Button
            onClick={handleClose}
            border
            buttonType="weak"
            buttonSize="small"
            style={{ width: 32, height: 32, padding: 0 }}
            loading={loading}
          >
            ✕
          </Button>
        </Flex>
      }
    >
      <Flex direction="column" gap={8} style={{ width: '100%' }}>
        <Flex
          direction="column"
          gap={2}
          style={{
            backgroundColor: Colors.gray30,
            padding: 12,
            borderRadius: 12,
            width: '100%',
          }}
        >
          <Text
            textStyle="13_400"
            style={{
              color: Colors.gray600,
            }}
          >
            {certificate.certificateOwner}
          </Text>
          <Text textStyle="13_400" style={{ color: Colors.gray600 }}>
            {certificate.certificateAuthority || '금융결제원'} ·{' '}
            {certificate.certificateType || '전자세금용'}
          </Text>
          <Text textStyle="13_400" style={{ color: Colors.gray600 }}>
            만료일 {certificate.certificateExpireDate}
          </Text>
        </Flex>

        <Text textStyle="14_400" style={{ color: Colors.gray700 }}>
          공동인증서의 사업자등록번호를 입력해 주세요.
        </Text>

        <Flex direction="column" gap={8} style={{ width: '100%' }}>
          <Text textStyle="14_400" style={{ color: 'rgba(0,0,0,0.88)' }}>
            사업자등록번호
          </Text>
          <Dropdown
            menu={{
              items:
                businessNumbersData?.Data?.data?.businessRegNumbers?.map(
                  (item: BusinessRegistrationNumberInfo) => ({
                    key: item.businessRegNumber,
                    label: item.companyName
                      ? `${formatCompanyTaxNumber(item.businessRegNumber)} (${item.companyName})`
                      : formatCompanyTaxNumber(item.businessRegNumber),
                    onClick: () => handleBusinessNumberSelect(item.businessRegNumber),
                  }),
                ) || [],
            }}
            trigger={['click']}
          >
            <CDSInput
              value={businessRegNumber}
              onChange={(e) => {
                const value = e.target.value;
                if (value.length > 10) {
                  return;
                }
                handleBusinessNumberChange(value);
              }}
              formatter={formatCompanyTaxNumber}
              deformatter={deformatCompanyTaxNumber}
              inputSize="default"
              inputStatus={businessNumberError ? 'error' : undefined}
              additionalText={businessNumberError}
              placeholder="사업자등록번호 10자리"
            />
          </Dropdown>
        </Flex>

        <Flex justify="flex-end" gap={8} style={{ paddingTop: 8 }}>
          <Button
            border
            onClick={handleClose}
            buttonType="weak"
            style={{ width: 60 }}
            disabled={loading}
          >
            닫기
          </Button>
          <Button
            onClick={handleConfirm}
            buttonType="primary"
            style={{ width: 60 }}
            disabled={!validation.isValid || loading}
            loading={loading}
          >
            확인
          </Button>
        </Flex>
      </Flex>
    </CommonModal>
  );
};

export default BusinessRegNumberInputModal;
